import { LitElement, html } from 'lit';
import { store } from '../store.js';
import { getCategories, normalizeItemName } from '../utils/units.js';
import { importRecipeFromUrl } from '../utils/recipe-scraper.js';
import { showLoading, hideLoading } from '../components/loading-spinner.js';
import { showSuccess, showError } from '../components/toast-notification.js';

class RecipeFormView extends LitElement {
  static properties = {
    recipeId: { type: String },
    _ingredients: { state: true },
  };

  createRenderRoot() { return this; }

  constructor() {
    super();
    this.recipeId = null;
    this._ingredients = [];
    this._initialized = false;
  }

  get _isEditing() {
    return !!this.recipeId;
  }

  updated(changedProps) {
    if (!this._initialized) {
      this._initialized = true;
      if (this._isEditing) {
        this._ingredients = [...store.getRecipeIngredients(this.recipeId)];
      }
    }
  }

  _addIngredient() {
    this._ingredients = [...this._ingredients, { item: '', quantity: '', unit: '', category: '' }];
  }

  _removeIngredient(index) {
    this._ingredients = this._ingredients.filter((_, i) => i !== index);
  }

  _updateIngredient(index, field, value) {
    const updated = [...this._ingredients];
    updated[index] = { ...updated[index], [field]: value };
    this._ingredients = updated;
  }

  async _handleImportUrl() {
    const urlInput = this.renderRoot.querySelector('#recipe-url');
    const url = urlInput?.value.trim();
    if (!url) { showError('Please enter a recipe URL'); return; }

    try {
      showLoading();
      const imported = await importRecipeFromUrl(url);

      const form = this.renderRoot.querySelector('#recipe-form');
      form.querySelector('#name').value = imported.name || '';
      form.querySelector('#cook_time_mins').value = imported.cook_time_mins || '';
      form.querySelector('#servings').value = imported.servings || 4;
      form.querySelector('#source').value = imported.source || url;
      form.querySelector('#instructions').value = imported.instructions || '';

      this._ingredients = imported.ingredients || [];
      hideLoading();

      if (this._ingredients.length === 0) {
        showError(`Recipe "${imported.name}" imported but no ingredients found. Add them manually.`);
      } else {
        showSuccess(`Recipe imported! Found ${this._ingredients.length} ingredients`);
      }
      urlInput.value = '';
    } catch (error) {
      console.error('Import error:', error);
      hideLoading();
      showError(error.message || 'Failed to import recipe.');
    }
  }

  async _handleSubmit(e) {
    e.preventDefault();
    const form = this.renderRoot.querySelector('#recipe-form');
    const formData = new FormData(form);

    const name = formData.get('name')?.trim();
    const cook_time_mins = formData.get('cook_time_mins');
    const servings = formData.get('servings');
    const instructions = formData.get('instructions')?.trim();

    if (!name) { showError('Recipe name is required'); return; }
    if (!cook_time_mins) { showError('Cook time is required'); return; }
    if (!servings) { showError('Servings is required'); return; }
    if (!instructions) { showError('Instructions are required'); return; }

    const recipeData = {
      name,
      cook_time_mins,
      servings,
      source: formData.get('source') || '',
      instructions,
    };

    const validIngredients = this._ingredients.filter(ing => ing.item);

    if (validIngredients.length === 0) {
      showError('Please add at least one complete ingredient');
      return;
    }

    try {
      showLoading();

      if (this._isEditing) {
        await store.updateRecipe(this.recipeId, recipeData);
        const ingredientsWithId = validIngredients.map(ing => ({
          recipe_id: this.recipeId,
          item: normalizeItemName(ing.item),
          quantity: ing.quantity,
          unit: ing.unit,
          category: ing.category,
        }));
        await store.saveRecipeIngredients(this.recipeId, ingredientsWithId);
        showSuccess('Recipe updated successfully');
        window.location.hash = `#/recipes/${this.recipeId}`;
      } else {
        const newRecipe = await store.addRecipe(recipeData);
        const ingredientsWithId = validIngredients.map(ing => ({
          recipe_id: newRecipe.id,
          item: normalizeItemName(ing.item),
          quantity: ing.quantity,
          unit: ing.unit,
          category: ing.category,
        }));
        await store.saveRecipeIngredients(newRecipe.id, ingredientsWithId);
        showSuccess('Recipe added successfully');
        window.location.hash = `#/recipes/${newRecipe.id}`;
      }
    } catch (error) {
      console.error('Error saving recipe:', error);
      showError('Failed to save recipe');
    } finally {
      hideLoading();
    }
  }

  render() {
    const recipe = this._isEditing ? store.getRecipe(this.recipeId) : null;

    if (this._isEditing && !recipe) {
      return html`
        <div class="max-w-7xl mx-auto px-4 py-6 md:ml-64">
          <div class="text-center py-12">
            <p class="text-gray-500 mb-4">Recipe not found</p>
            <a href="#/recipes" class="text-blue-600 hover:underline">Back to recipes</a>
          </div>
        </div>
      `;
    }

    const categories = getCategories();

    return html`
      <div class="max-w-4xl mx-auto px-4 py-6 md:ml-64">
        <a href="${this._isEditing ? `#/recipes/${this.recipeId}` : '#/recipes'}" class="inline-flex items-center text-gray-600 hover:text-gray-900 mb-6">
          <svg class="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"/></svg>
          Back
        </a>

        <h1 class="text-3xl font-bold text-gray-900 mb-6">${this._isEditing ? 'Edit Recipe' : 'Add New Recipe'}</h1>

        <form id="recipe-form" @submit=${this._handleSubmit} class="space-y-6">
          ${!this._isEditing ? html`
            <div class="bg-blue-50 border border-blue-200 rounded-lg p-6">
              <h2 class="text-xl font-semibold text-gray-900 mb-4">Import from URL</h2>
              <p class="text-sm text-gray-600 mb-4">Paste a recipe URL to automatically import ingredients and instructions</p>
              <div class="flex gap-3">
                <input type="url" id="recipe-url" placeholder="https://example.com/recipe" class="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <button type="button" @click=${this._handleImportUrl} class="bg-blue-600 text-white px-6 py-2 rounded-lg font-semibold hover:bg-blue-700 transition-colors">Import Recipe</button>
              </div>
            </div>
          ` : ''}

          <div class="bg-white rounded-lg shadow p-6">
            <h2 class="text-xl font-semibold text-gray-900 mb-4">Basic Information</h2>
            <div class="space-y-4">
              <div>
                <label for="name" class="block text-sm font-medium text-gray-700 mb-2">Recipe Name <span class="text-red-600">*</span></label>
                <input type="text" id="name" name="name" required .value=${recipe?.name || ''} class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              </div>
              <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label for="cook_time_mins" class="block text-sm font-medium text-gray-700 mb-2">Cook Time (minutes) <span class="text-red-600">*</span></label>
                  <input type="number" id="cook_time_mins" name="cook_time_mins" required min="1" .value=${recipe?.cook_time_mins || ''} class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                </div>
                <div>
                  <label for="servings" class="block text-sm font-medium text-gray-700 mb-2">Servings <span class="text-red-600">*</span></label>
                  <input type="number" id="servings" name="servings" required min="1" step="0.5" .value=${recipe?.servings || '4'} class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                </div>
              </div>
              <div>
                <label for="source" class="block text-sm font-medium text-gray-700 mb-2">Source (optional)</label>
                <input type="text" id="source" name="source" .value=${recipe?.source || ''} placeholder="e.g., Mom's cookbook, Food Network" class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
              </div>
              <div>
                <label for="instructions" class="block text-sm font-medium text-gray-700 mb-2">Instructions <span class="text-red-600">*</span></label>
                <textarea id="instructions" name="instructions" required rows="8" class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">${recipe?.instructions || ''}</textarea>
              </div>
            </div>
          </div>

          <div class="bg-white rounded-lg shadow p-6">
            <h2 class="text-xl font-semibold text-gray-900 mb-4">Ingredients</h2>
            <div class="space-y-3 mb-4">
              ${this._ingredients.length === 0 ? html`<p class="text-gray-500">No ingredients added yet</p>` : ''}
              ${this._ingredients.map((ing, index) => html`
                <div class="grid grid-cols-12 gap-2 items-start">
                  <div class="col-span-12 md:col-span-4">
                    <input type="text" placeholder="Item name" .value=${ing.item || ''} @input=${(e) => this._updateIngredient(index, 'item', e.target.value)} class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  </div>
                  <div class="col-span-6 md:col-span-2">
                    <input type="text" placeholder="Qty" .value=${ing.quantity || ''} @input=${(e) => this._updateIngredient(index, 'quantity', e.target.value)} class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  </div>
                  <div class="col-span-6 md:col-span-2">
                    <input type="text" placeholder="Unit" .value=${ing.unit || ''} @input=${(e) => this._updateIngredient(index, 'unit', e.target.value)} class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                  </div>
                  <div class="col-span-10 md:col-span-3">
                    <select @change=${(e) => this._updateIngredient(index, 'category', e.target.value)} class="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                      <option value="">Category</option>
                      ${categories.map(cat => html`<option value=${cat} ?selected=${ing.category === cat}>${cat}</option>`)}
                    </select>
                  </div>
                  <div class="col-span-2 md:col-span-1">
                    <button type="button" @click=${() => this._removeIngredient(index)} class="w-full px-3 py-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors">
                      <svg class="w-5 h-5 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/></svg>
                    </button>
                  </div>
                </div>
              `)}
            </div>
            <button type="button" @click=${this._addIngredient} class="text-blue-600 hover:text-blue-700 font-medium flex items-center">
              <svg class="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/></svg>
              Add Ingredient
            </button>
          </div>

          <div class="flex gap-3">
            <button type="submit" class="flex-1 bg-blue-600 text-white py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors">
              ${this._isEditing ? 'Update Recipe' : 'Save Recipe'}
            </button>
            <a href="${this._isEditing ? `#/recipes/${this.recipeId}` : '#/recipes'}" class="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors text-center">
              Cancel
            </a>
          </div>
        </form>
      </div>
    `;
  }
}

customElements.define('recipe-form-view', RecipeFormView);
